import ContentsService, {
  SupportType,
  SupportTypeHelper,
} from "@/services/ContentsService";
import { BhapticsContent } from "@/store/reducers/contents";
import DeviceUtil from "@/utils/DeviceUtil";
import { useTrans } from "@/utils/useTrans";
import BlockIcon from "@/v2-components/core/icon/block-icon";
import GamesIcon from "@/v2-components/core/icon/games-icon";
import InfoIcon from "@/v2-components/core/icon/info-icon";
import TagIcon from "@/v2-components/core/icon/tag-icon";
import VestDeviceIcon from "@/v2-components/core/icon/VestDeviceIcon";
import { Tooltip } from "@/v2-components/ui/tooltip";
import React, { Fragment } from "react";
import Link from "next/link";
import { SupportTypeUtil } from "@/utils/SupportTypeUtil";

interface Props {
  game: BhapticsContent;
}
export default function GameInfo({ game }: Props) {
  const { t } = useTrans(["games"]);
  const supportType = SupportTypeUtil.getSupportType(game.supportedPlatforms);

  return (
    /**
     * 시안 데스크탑 `30035:22040` · 모바일 `30035:21525` — 우측 aside 카드.
     *
     * ★종전엔 **본문 전체폭 4열 그리드**(`grid-cols-[20%_20%_20%_40%]` + 세로 구분선)
     * 였다. 개정 시안은 같은 4개 항목을 **사이드바 카드**에 세로로 쌓는다.
     *
     * ## ★껍데기가 폭마다 다르다 — 모바일은 **카드가 아니다**
     *
     * 두 판을 각각 떠서 대조하면(design_context 실측 2026-09-03):
     *
     * ```
     *                    데스크탑 30035:22040      모바일 30035:21525
     *   껍데기            border #dde1e6 r16 p32   ★없음 (px16 · 세로 패딩 0)
     *   「게임 정보」 라벨   12/16 SemiBold tr .4     ★16/22 Regular tr .5
     *   항목 간 · 아이콘↔글자 · 라벨↔값              16 · 14 · 8 (공통)
     *   라벨 12/16 SemiBold #697077 · 값 14/18 #343a3f (공통)
     * ```
     *
     * ⚠️ **모바일에서 `px-4` 를 여기에 주면 안 된다** — 시안의 `px-16` 은 페이지
     * 거터이고, 이 리포에서는 부모 컨테이너가 이미 `mx-4` 로 그걸 준다(실측 BEFORE
     * 360: 카드 `x=16 w=328`). 여기서 또 주면 16 이 두 번 들어간다.
     *
     * ★**「게임 정보」 라벨이 두 폭 다 빠져 있었다.** 시안에는 데스크탑·모바일 양쪽에
     * 있다(`30035:22042` / `30035:21527`). 껍데기가 사라지는 모바일에서는 이 라벨이
     * 유일한 구분자라 더 필요하다.
     *
     * ⚠️ **항목·아이콘·데이터 소스는 그대로 둔다** — 껍데기만 바꾼다. 지원 유형의
     * 툴팁과 「See how to play guide」 는 시안에 안 보이지만 기능이라 유지한다
     * (`docs/pitfalls.md` #27 — UI 를 갈아치우면 조건부 UI 가 조용히 사라진다).
     */
    <div className="flex flex-col gap-4 laptop:rounded-2xl laptop:border laptop:border-coolgray-20 laptop:p-8">
      {/* `30035:21527` 16/22 Regular tr.5 · `30035:22042` 12/16 SemiBold tr.4 — 색은 공통. */}
      <h2 className="text-lg tracking-[0.5px] text-coolgray-60 laptop:text-sm laptop:font-600 laptop:tracking-[0.4px]">
        {t("Game Info")}
      </h2>
      {/*<div className="grid grid-cols-1 gap-5 laptop:grid-cols-4 laptop:gap-6">*/}
      <div className="flex gap-[14px]">
        <GamesIcon className="shrink-0" />
        <div className="mt-0 flex flex-col gap-2">
          <h3 className="text-sm font-600 tracking-[0.4px] text-coolgray-60">
            {t("Supported Platform")}
          </h3>
          <ul className="flex flex-col gap-0 text-base tracking-[0.25px] text-coolgray-80">
            {/**
             * ★**`enabled` 로 거른다** (#5708).
             *
             * admin 이 플랫폼 행을 끄면 목록·필터·SEO·판정은 전부 반영되는데 **상세
             * 사이드바에만 남아** 있었다. 그 플랫폼은 이미 광고를 멈추기로 한 것이고
             * `p.link` 는 내려간 스토어 페이지를 가리킬 수 있다.
             *
             * 리포에서 같은 필터를 거는 곳(2026-09-10 실측 · `git grep '=> p.enabled'`):
             *
             * ```
             *   GameCard.tsx:441            목록 카드
             *   GameCardWebView.tsx:392     웹뷰 카드
             *   BhapticsContentCards.tsx:59 플랫폼 필터
             *   game-seo.ts:117             메타 설명
             *   ContentsService.ts:141,150  Overwolf · SimRacing 판정
             * ```
             *
             * 여섯 곳이 걸고 **여기와 `GameInfoWebView` 둘만** 안 걸고 있었다.
             *
             * ⚠️ prod 엔 `enabled=false` 행이 **0건**이라 증상이 없다. 데이터가
             * 런타임에 오므로 tsc·eslint 로는 잡히지 않는 종류다.
             *
             * ★**모집단은 상세 경로다.** 이 컴포넌트의 데이터는 목록
             * (`/api/games/by-filters/`)이 아니라 **`by-slug`** 로 온다
             * (`pages/games/experiences/[slug]/index.tsx` → `pages/api/games/[slug].ts`
             * → `bhaptics-contents/by-slug/${slug}`). 목록 엔드포인트가 서버에서 이미
             * 거를 수도 있어 **잠복 근거로는 모집단이 다르다** (리뷰 #5715 R1 s2).
             * 같은 페이지 `[slug]/index.tsx` 의 형제 주석이 그 모집단으로 재 뒀다 —
             * `supportedPlatforms 559개 중 { true: 559 }` (2026-09-02).
             */}
            {game.supportedPlatforms
              .filter((p) => p.enabled)
              .map((p) => {
                return (
                  <Link key={p.platform} href={p.link} target="_blank">
                    <li>{ContentsService.friendlyPlatformName(p.platform)}</li>
                    <span className="last:hidden laptop:hidden">•</span>
                  </Link>
                );
              })}
          </ul>
        </div>
      </div>
      <div className="flex gap-[14px]">
        <VestDeviceIcon className="h-6 w-6 shrink-0" />
        <div className="mt-0 flex flex-col gap-2">
          <h3 className="text-sm font-600 tracking-[0.4px] text-coolgray-60">
            {t("Supported Device")}
          </h3>
          <ul className="flex flex-col gap-0 text-base tracking-[0.25px] text-coolgray-80">
            {game.compatibleDevices.map((device) => {
              return (
                <Fragment key={device}>
                  <li key={device}>{DeviceUtil.getFriendlyName(device)}</li>
                  <span className="last:hidden laptop:hidden">•</span>
                </Fragment>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="flex gap-[14px]">
        <TagIcon className="shrink-0" />
        <div className="mt-0 flex flex-col gap-2">
          <h3 className="text-sm font-600 tracking-[0.4px] text-coolgray-60">
            {t("Genre")}
          </h3>
          <ul className="flex flex-col gap-0 text-base tracking-[0.25px] text-coolgray-80">
            {game.categories.map((cate) => {
              return (
                <Fragment key={cate}>
                  <li className="capitalize" key={cate}>
                    {cate}
                  </li>
                  <span className="last:hidden laptop:hidden">•</span>
                </Fragment>
              );
            })}
          </ul>
        </div>
      </div>
      <div className="flex gap-[14px]">
        <BlockIcon className="shrink-0" />
        <div className="mt-0 flex flex-col gap-2 bg-white">
          <h3 className="text-sm font-600 tracking-[0.4px] text-coolgray-60">
            {t("Support Type")}
          </h3>

          <div className="flex flex-wrap items-center text-base tracking-[0.25px] text-coolgray-80">
            <span className="mr-1">
              {SupportTypeHelper.asDisplay(supportType)}
            </span>
            {SupportTypeHelper.isMod(supportType) && (
              <>
                <Tooltip
                  delayDuration={0}
                  tooltipContent={
                    <div className="whitespace-pre-line">
                      {supportType === SupportType.OfficialMod
                        ? "Official haptics made by bHaptics for supported games."
                        : `Community-made haptics for\n games without official\n support.`}
                    </div>
                  }
                  align="start"
                  side="bottom"
                  alignOffset={-40}
                >
                  <div>
                    <InfoIcon className="text-coolgray-50" />
                  </div>
                </Tooltip>
              </>
            )}
          </div>
          {/**
           * ★**가이드 iframe 을 걷었다** (#5474).
           *
           * 종전엔 `guideSummary` 가 `/docs/` URL 이면 이 카드 안에
           * `<iframe src="/docs/{id}?theme=white&embed=1">` 를 그렸다.
           * `open={!isMobileView || open}` 이라 **데스크탑에선 항상 열림**이었다.
           *
           * 개정 시안(`30035:22039`)의 카드에는 그 블록이 없고, 실제로 쓸 수도 없었다 —
           * 실측(2026-09-01, 1920 뷰포트): `h-auto`·`w-full` 이 안 먹어 iframe 이
           * **300×150**(브라우저 기본값)으로 떠서 문서가 작은 창에 갇혔다.
           * 종전 전체폭 4열 그리드에서는 넓었는데 카드(424 − p32×2 = 360)로 좁아지며
           * 더 나빠졌다.
           *
           * 가이드 경로는 **없애지 않고 옮겼다** — aside 최상단 `GameGuideButtons` 의
           * 「How to Play」 가 `guideLink → guideSummary` 순으로 연다. prod 158건 중
           * 비-Mod 23건은 종전 게이트로 버튼이 없었어서, 게이트도 함께 넓혔다.
           */}
        </div>
      </div>
    </div>
  );
}
